// src/components/PriceBreakdownCard.jsx
import React, { useState } from "react";
import { X, Tag, CheckCircle } from "lucide-react";

const PriceBreakdownCard = ({
  originalPrice,
  offerDiscount,
  registration,
  deposit,
  onCancel,
  onProceed,
  onApplyCoupon,
  onRemoveCoupon,
  initialCouponCode = "",
  initialCouponDiscount = 0,
}) => {
  const [couponCode, setCouponCode] = useState(initialCouponCode);
  const [couponDiscount, setCouponDiscount] = useState(initialCouponDiscount);

  const discountedPrice = originalPrice - offerDiscount;
  const finalPrice = discountedPrice - couponDiscount + registration + deposit;
  const isApplied = couponDiscount > 0;

  const handleApply = async () => {
    const discount = await onApplyCoupon(couponCode);
    setCouponDiscount(discount);
  };

  const handleRemove = () => {
    setCouponCode("");
    setCouponDiscount(0);
    onRemoveCoupon();
  };

  return (
    <div className="bg-gradient-to-br from-primary-shade1/20 via-white to-primary-shade1/10 p-4 rounded-2xl shadow-3d backdrop-blur-xs border border-white/50 w-full max-w-sm transition-all hover:shadow-primary/20 hover:-translate-y-1">
      <h3 className="text-lg font-semibold mb-3 text-center bg-gradient-to-r from-primary-shade4 to-primary bg-clip-text text-transparent">
        Price Breakdown
      </h3>

      <div className="bg-white/50 p-3 rounded-xl space-y-2 text-sm backdrop-blur-xs">
        <div className="flex justify-between text-muted">
          <span>Base Price</span>
          <span>₹{originalPrice}</span>
        </div>

        {offerDiscount > 0 && (
          <div className="flex justify-between text-green-600 text-[0.9em] animate-fade-in">
            <span>Offer Discount</span>
            <span>- ₹{offerDiscount}</span>
          </div>
        )}

        <div className="space-y-1.5">
          <div className="flex gap-1.5 items-center">
            <div className="relative flex-1">
              <Tag className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 text-gray-400" />
              <input
                type="text"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value)}
                placeholder="Coupon code"
                disabled={isApplied}
                className="w-full pl-8 pr-20 py-2 rounded-md border-2 focus:border-primary-shade3 focus:ring-1 ring-primary/20 transition-all text-[0.9em]"
              />
              {!isApplied ? (
                <button
                  onClick={handleApply}
                  disabled={!couponCode.trim()}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 bg-gradient-to-r from-primary-shade2 to-primary-shade4 text-white px-3 py-1 rounded-md text-[0.9em] disabled:opacity-50 hover:shadow-primary/20 hover:shadow-sm transition-all"
                >
                  Apply
                </button>
              ) : (
                <button
                  onClick={handleRemove}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <CheckCircle className="w-4 text-green-500" />
                </button>
              )}
            </div>
          </div>

          {couponDiscount > 0 && (
            <div className="flex justify-between text-green-600 text-[0.9em] animate-fade-in">
              <span>Coupon Discount</span>
              <span>- ₹{couponDiscount}</span>
            </div>
          )}
        </div>

        <div className="space-y-1 pt-1">
          <div className="flex justify-between text-muted">
            <span>Registration</span>
            <span>₹{registration}</span>
          </div>
          <div className="flex justify-between text-muted">
            <span>Deposit</span>
            <span>₹{deposit}</span>
          </div>
        </div>

        <hr className="border-t border-dashed border-gray-100 my-2" />

        <div className="flex justify-between font-semibold text-sm bg-primary-shade1/30 p-2 rounded-md">
          <span>Total Payable</span>
          <span className="text-primary-shade5">₹{finalPrice}</span>
        </div>
      </div>

      <div className="flex justify-between items-center mt-4">
        <button
          onClick={onCancel}
          className="text-xs text-primary-shade4 font-medium hover:text-primary-shade5 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={onProceed}
          className="bg-gradient-to-r from-primary-shade3 to-primary-shade5 text-white px-5 py-2 rounded-lg text-sm font-medium hover:shadow-primary/30 hover:shadow-md transition-all"
        >
          Pay Now
        </button>
      </div>
    </div>
  );
};

export default PriceBreakdownCard;